import values from 'lodash/values';
import invert from 'lodash/invert';
import snakeCase from 'lodash/snakeCase';

import {Bathtub} from "./parts/bathtub";
import FloorNew from "./parts/floorNew";
import {Vanity} from "./parts/vanity";
import {Window} from "./parts/window";
import {Ceiling} from "./parts/ceiling";
import {Toilet} from "./parts/toilet";
import {Shower} from "./parts/shower";
import {ShowerTubCombo} from "./parts/showerTubCombo";
import GlobalShadow from "./parts/globalShadow";
import {Wall} from "./parts/wall";
import ExternallWall from "./subparts/wall/externallWall";
import Layer from "../../visComponents/layer";
import Z from './parts/zIndex';
import {LayoutType, LayoutAbbreviation, bathTubTypeToString, ShowerComboTubType}
  from "./constants";

import {
  largeLayoutShadow,
  smallLayoutShadow,
  shower30inWallShadow,
  LayoutThumbnails
} from './assets';
import LayoutCommon from "../../visComponents/layoutCommon";
import Other from "./parts/other";
import NonVisualizerSubPart from "./subparts/nonVisualizerSubPart";
import DesignNavigationItem from "../../visComponents/designNavigationItem";
import {ShowerAccessories} from "./parts/showerAccessories";
import store from "../../../store";
import {AVAILABLE_VISUALIZER_TYPES} from "../../../config";

export class Layout extends LayoutCommon {

  /**
   * @type {int}
   */
  defaultType = LayoutType.Large;
  type = this.defaultType;
  localStorageKey = "bvState";
  layersBinding = "Bathroom";
  visualizerType = AVAILABLE_VISUALIZER_TYPES.Bathroom;
  layoutNames = LayoutAbbreviation;
  layoutTypes = LayoutType;
  layoutThumbnails = LayoutThumbnails;
  zIndexes = Z.layouts;

  constructor(props) {
    super(props);
    this.parts[Vanity.id]         = new Vanity();
    this.parts[Shower.id]         = new Shower();
    this.parts[ShowerTubCombo.id] = new ShowerTubCombo();
    this.parts[ShowerAccessories.id] = new ShowerAccessories();
    this.parts[Bathtub.id]        = new Bathtub();
    this.parts[Toilet.id]         = new Toilet();
    this.parts[Wall.id]           = new Wall();  // wall should render it's layers only after vanity & shower
    this.parts[FloorNew.id]       = new FloorNew();
    this.parts[Ceiling.id]        = new Ceiling();
    this.parts[Window.id]         = new Window();
    this.parts[GlobalShadow.id]   = new GlobalShadow();
    this.parts[Other.id] = new Other();

    values(this.parts).forEach(part => part.layout = this);
  }

  resetEverything() {
    const features = this.getFeatures();
    const {activeQuoteIndex} = store.getState().quotesState;
    features.forEach(parentGroup => {
      if (parentGroup instanceof Shower) {
        parentGroup.activeSubType_ = "Custom";
      } else if (parentGroup instanceof ShowerTubCombo) {
        parentGroup.showerTubComboIsActive = false;
        parentGroup.bottomTub = ShowerComboTubType.bathtub_liner;
      }
      parentGroup.setDefault(this);
      parentGroup.placeholderChanged = false;
      parentGroup.firstClick[activeQuoteIndex] = false;
      // if (parentGroup instanceof Shower && parentGroup.showerBody.featuresParts) {
      //   parentGroup.showerBody.featuresParts = {};
      // }
      if (parentGroup.relatedItems && parentGroup.relatedItems.length) {
        parentGroup.relatedItems.forEach((subPart) => {
          if (subPart.partId !== 'shower_body') {
            subPart.placeholderChanged = false;
            subPart.firstClick[activeQuoteIndex] = false;
          }
          subPart.disabled = subPart.defaultDisabled;
          if (Array.isArray(subPart.activeFeatureParts) && subPart.activeFeatureParts.length) {
            subPart.activeFeatureParts = subPart.setActiveFeatureParts();
          }
          if (subPart instanceof ExternallWall) {
            subPart.selectedByUser = false
          }
        });
      }
    });
    this.changeLayout(features);
  }

  getCurrentLayoutName(type = this.type) {
    return invert(LayoutType)[type];
  }

  getCurrentLayoutAbbreviation() {
    return LayoutAbbreviation[this.getCurrentLayoutName()]
  }

  addCustomLayers = (res) => {
    // add global shadow
    const globalShadowParent = this.parts[GlobalShadow.id]; // if no global shadow uploaded - take the hardcoded one
    if (!globalShadowParent.categoryEnabled || (globalShadowParent.activeFeature
      && (!globalShadowParent.activeFeature.layer || !globalShadowParent.activeFeature.layer.src))) {
      if (this.type === LayoutType.Large) {
        res.push(new Layer("bv", Z.layouts.Large.Global_Shadow_Overlay, largeLayoutShadow, false));
      } else if (this.type === LayoutType.Small) {
        res.push(new Layer("bv", Z.layouts.Small.Global_Shadow_Overlay, smallLayoutShadow, false));
      }
    }

    // add right ex wall shadow for complete showers
    const shower = this.parts[Shower.id];
    if (shower.showerSize() === "30in" && shower.activeSubType_ === "Complete" && !shower.showerTubComboIsActive() && this.type === LayoutType.Large) {
      res.push(new Layer("bv", Z.layouts.Large.Shower.Custom60in.ExtrusionWallRightShadow, shower30inWallShadow, false));
    }
    return res
  }

  /**
   * Sets bv features from url
   */

  parseUrl(bvState) {
    if (bvState.disabled_parts) {
      bvState.disabled_parts = bvState.disabled_parts.split(",");
    }
    if (bvState.shower_floor_is_active) {
      bvState.shower_floor_is_active = bvState.shower_floor_is_active === "true"
    }
    if (bvState.shower_tub_combo_is_active) {
      bvState.shower_tub_combo_is_active = bvState.shower_tub_combo_is_active === "true"
    }
    if (bvState.shower_interior_wall_accent_parts) {
      bvState.shower_interior_wall_accent_parts = Number.isInteger(bvState.shower_interior_wall_accent_parts)
        ? [bvState.shower_interior_wall_accent_parts]
        : bvState.shower_interior_wall_accent_parts.split(",")
    }
    if (bvState.shower_interior_wall_parts) {
      bvState.shower_interior_wall_parts = Number.isInteger(bvState.shower_interior_wall_parts)
        ? [bvState.shower_interior_wall_parts]
        : bvState.shower_interior_wall_parts.split(",")
    }
    if (bvState.left_shower_wall_type) {
      switch (bvState.left_shower_wall_type) {
        case 30:
        case "30":
        case "30X8":
          bvState.left_shower_wall_type = "30X8";
          break;
        case 15:
        case "15":
        case "15X4":
          bvState.left_shower_wall_type = "15X4";
          break;
        case "Pony":
          bvState.left_shower_wall_type = "Pony";
          break;
      }
    }
    if (bvState["caddie_type"] && this.parts[ShowerAccessories.id]) {
      this.parts[ShowerAccessories.id].caddie_type = bvState["caddie_type"];
    }
    return bvState
    // console.log('restoring bvState from url...', bvState);
  }

  /**
   *
   * @param bvState {object}
   * @param setCartAndUserState {boolean}
   * @param useNames {boolean}
   * @param needToChangeLayout {boolean}
   * @param firstLoad {boolean}
   */
  setActiveFeaturesFromObject(bvState, setCartAndUserState = false, useNames = false, needToChangeLayout = true, firstLoad = false) {
    if (bvState) {

      const layout_type = +bvState.layout_type;
      needToChangeLayout = needToChangeLayout && (layout_type || layout_type === 0) && (layout_type !== +this.type);

      if (needToChangeLayout) {
        this.type = layout_type;
      }

      const vanity = this.parts[Vanity.id];
      const shower = this.parts[Shower.id];
      const showerTubCombo = this.parts[ShowerTubCombo.id];
      // const bathtub = this.parts[Bathtub.id];

      // to avoid checks below
      bvState.disabled_parts = bvState.disabled_parts || [];

      // const {activeQuoteIndex} = store.getState().quotesState;

      this.getFeatures()
        .forEach((parentGroup) => {
          if (parentGroup.menuType === 0 && parentGroup.partName !== "Toilet") {
            const partId = parentGroup.partId;
            const hasPreviouslySavedItem = this.selectedLayoutFeatures[layout_type]
              && this.selectedLayoutFeatures[layout_type][partId];
            if (bvState.hasOwnProperty(partId) || hasPreviouslySavedItem) {
              if (useNames) {
                parentGroup.activeFeature = parentGroup.getFeatureByPartName(bvState[partId]);
                if (!parentGroup.activeFeature && hasPreviouslySavedItem) {
                  parentGroup.activeFeature = parentGroup.getFeatureByPartName(hasPreviouslySavedItem);
                }
              } else {
                parentGroup.activeFeature = parentGroup.getFeatureById(bvState[partId])
                  || parentGroup.getDefaultFeature();
              }
              parentGroup.placeholderChanged = !!parentGroup.activeFeature;
              // parentGroup.firstClick[activeQuoteIndex] = !!parentGroup.activeFeature; // this makes steps selected! removed
            }
            if (parentGroup.activeFeature && firstLoad && !parentGroup.activeFeature.notAvailableInVisualizer) { // Leave non in vis features locked
              parentGroup.activeFeature.dbFeature.unlocked = true;
            }
            parentGroup.disabled = bvState.disabled_parts.includes(partId)
          } else {
            let subParts = parentGroup.relatedItems;
            subParts.forEach((subPart) => {
              const partId = subPart.partId;
              const hasPreviouslySavedItem = this.selectedLayoutFeatures[layout_type]
                && this.selectedLayoutFeatures[layout_type][partId];
              if (bvState.hasOwnProperty(partId) || hasPreviouslySavedItem) {
                if (useNames) {
                  subPart.activeFeature = subPart.getFeatureByPartName(bvState[partId]);
                  if (!subPart.activeFeature && hasPreviouslySavedItem) {
                    subPart.activeFeature = subPart.getFeatureByPartName(hasPreviouslySavedItem);
                  }
                } else {
                  subPart.activeFeature = subPart.getFeatureById(bvState[partId]) || parentGroup.getDefaultFeature();
                }
                subPart.placeholderChanged = !!subPart.activeFeature;
                // subPart.firstClick[activeQuoteIndex] = !!subPart.activeFeature // this makes steps selected! removed
                if (partId === "shower_body") {
                  subPart.showerBodySelectedByUser = true
                }
              }
              if (subPart.activeFeature && firstLoad && !subPart.activeFeature.notAvailableInVisualizer) {
                subPart.activeFeature.dbFeature.unlocked = true;
              }
              if (firstLoad) {
                subPart.placeholderChanged = true;
              }
              subPart.disabled = bvState.disabled_parts.includes(partId);
            })
          }
        });

      const partPredicate = (part, value) => useNames ? part.partName === value : +part.dbFeature.id === +value;
      Object.keys(bvState)
        .forEach((bvStateKey) => {
          if (bvStateKey === "vanity_type") {
            vanity.activeSubType_ = bvState.vanity_type.charAt(0).toUpperCase() + bvState.vanity_type.substr(1);
          }
          if (bvStateKey === "shower_type") {
            shower.activeSubType_ = bvState.shower_type.charAt(0).toUpperCase() + bvState.shower_type.substr(1);
            if (bvState.shower_type === "complete" && bvState.shower_parts.length) {
              shower.showerBody.activeFeatureParts = bvState.shower_parts.map(shower_part_id => {
                return shower.showerBody.featuresParts.find(_ => partPredicate(_, shower_part_id));
              });
            }
          }
          if (bvStateKey === "shower_interior_wall_parts") {
            if (bvState.shower_interior_wall_parts.length && shower.showerInteriorWall) {
              shower.showerInteriorWall.activeFeatureParts = bvState.shower_interior_wall_parts
                .map((shower_accent_wall_part_id) => {
                  let accordingPart = shower.showerInteriorWall.featuresParts.find(_ => partPredicate(_, shower_accent_wall_part_id));
                  if (!accordingPart) {
                    const showerFeature = shower.showerInteriorWall.features.find(_ => partPredicate(_, bvState.shower_interior_wall));
                    if (showerFeature) {
                      const availableParts = shower.showerInteriorWall.setActiveFeatureParts(showerFeature);
                      if (availableParts.length) {
                        accordingPart = availableParts[0]
                      }
                    }
                  }
                  return accordingPart;
                });
            }
          }
          if (bvStateKey === "shower_interior_wall_accent_parts") {
            if (bvState.shower_interior_wall_accent_parts.length && shower.showerInteriorWallAccent) {
              shower.showerInteriorWallAccent.activeFeatureParts = bvState.shower_interior_wall_accent_parts
                .map((shower_accent_wall_part_id) => {
                  return shower.showerInteriorWallAccent.featuresParts.find(_ => partPredicate(_, shower_accent_wall_part_id));
                });
            }
          }
          if (bvStateKey === "shower_floor_is_active") {
            shower.showerFloorIsActive = bvState[bvStateKey];
          }
          if (bvStateKey === "shower_tub_combo_is_active") {
            showerTubCombo.showerTubComboIsActive = bvState[bvStateKey];
            if (bvState[bvStateKey] && bvState.bottom_tub) {
              showerTubCombo.bottomTub = bvState.bottom_tub
            }
            if (bvState[bvStateKey] && bvState.left_shower_wall_type) {
              showerTubCombo.leftShowerWallType = bvState.left_shower_wall_type
            }
          }
        });

      if (needToChangeLayout) {
        this.changeLayout(this.getFeatures());
      }

      if (setCartAndUserState) {
        this.setCartStateAndUserStateFromBvState(bvState);
      }
    }
  }

  /**
   *
   * @param bvState {object}
   * @param useNames {boolean}
   * @returns bvState {object}
   */
  addSpecificPropertiesToLocalStorage(bvState, useNames = false) {

    const bathTub = this.parts[Bathtub.id];
    const shower = this.parts[Shower.id];
    const showerTubCombo = this.parts[ShowerTubCombo.id];

    bvState.layout_type = this.type;

    Object.keys(bvState).forEach((bvStateKey) => {
      // console.log('bvStateKey', bvStateKey);
      if (bvStateKey === 'bathtub') {
        bvState.bathtub_type = bathTubTypeToString(bathTub.activeSubType_).toLowerCase();
      }
      if (bvStateKey === 'shower_body') {
        bvState.shower_type = shower.activeSubType_.toLowerCase();
        bvState.shower_floor_is_active = shower.showerFloorIsActive;
        if (bvState.shower_type === "complete") {
          bvState.shower_parts = shower.showerBody.activeFeatureParts
            .filter(activeFeaturePart => activeFeaturePart)
            .map(activeFeaturePart => {
              return useNames ? activeFeaturePart.partName : activeFeaturePart.dbFeature.id
            });
        }
        bvState.shower_tub_combo_is_active = shower.showerTubComboIsActive();
        if (bvState.shower_tub_combo_is_active) {
          bvState.bottom_tub = showerTubCombo.bottomTub;
          bvState.left_shower_wall_type = showerTubCombo.leftShowerWallType;
        }
      }
      if (bvStateKey === 'shower_interior_wall') {
        bvState.shower_interior_wall_parts = shower.showerInteriorWall.activeFeatureParts
          .filter(activeFeaturePart => activeFeaturePart)
          .map(activeFeaturePart => {
            return useNames ? activeFeaturePart.partName : activeFeaturePart.dbFeature.id
          });
      }
      if (bvStateKey === 'shower_interior_wall_accent') {
        bvState.shower_interior_wall_accent_parts = shower.showerInteriorWallAccent.activeFeatureParts
          .filter(activeFeaturePart => activeFeaturePart && activeFeaturePart.dbFeature)
          .map(activeFeaturePart => {
            return useNames ? activeFeaturePart.partName : activeFeaturePart.dbFeature.id
          });
      }
      if (bvStateKey === 'vanity') {
        bvState.vanity_type = this.parts[Vanity.id].activeSubType_.toLowerCase();
      }
    });
    // console.log('bvState', bvState);
    if (bvState["pony_wall"]) {
      if (!bvState["pony_wall_glass"] && shower.ponyWallGlass
        && shower.ponyWallGlass.activeFeature && shower.ponyWallGlass.activeFeature.dbFeature.id) {
        const feature = shower.ponyWallGlass.activeFeature;
        bvState["pony_wall_glass"] = useNames ? feature.partName : feature.dbFeature.id
      }
    }
    if (bvState["large_corner_caddie"] || bvState["corner_caddie"] || bvState["small_corner_shelf"]) {
      const showerAccessoriesParent = this.parts[ShowerAccessories.id];
      if (showerAccessoriesParent) {
        bvState.caddie_type = this.parts[ShowerAccessories.id].caddie_type;
      }
    }
    return bvState
  }

  addNonVisualizerSubPart(featureGroupName){
    const otherParent = this.parts[Other.id];
    const partId = snakeCase(featureGroupName);
    let relatedItem = otherParent.relatedItems.find(relatedItem => relatedItem.partId === partId);
    if (!relatedItem) {
      relatedItem = new NonVisualizerSubPart(true, partId, featureGroupName);
      relatedItem.getParent = () => otherParent;
      otherParent.relatedItems.push(relatedItem);
    }
    return new DesignNavigationItem(relatedItem.partName, relatedItem)
  }

  excludedIndexesFromHover = (layoutType) => {
    return [
      this.zIndexes[layoutType].Global_Shadow_Overlay,  // exclude Global Shadow
      this.zIndexes[layoutType].Window_Covering,  // exclude Window Coverings
      this.zIndexes[layoutType].Ceiling_Lights,  // exclude Ceiling_Lights
      // this.zIndexes[layoutType].Shower.Custom60in.Wall,  // exclude Shower Wall
      this.zIndexes[layoutType].Shower.Custom60in.Glass,  // exclude Shower Glass
      // && layerHovering.layer.zIndex !== Z.this.zIndexes[layoutType].Shower.Custom60in.ExtrusionWallRight  // exclude Shower ExtrusionWallRight
      // && layerHovering.layer.zIndex !== Z.this.zIndexes[layoutType].Shower.Custom60in.ExtrusionWallLeft  // exclude Shower ExtrusionWallLeft
      this.zIndexes[layoutType].Shower.Custom60in.Overlay,  // exclude Shower Overlay
      this.zIndexes[layoutType].Shower.Steam60in.Overlay,  // exclude Shower Overlay
      this.zIndexes[layoutType].Shower.Custom60in.ExtrusionWallRightShadow,  // exclude Right Shower Wall Shadow
      this.zIndexes[layoutType].Shower.Custom60in.ExtrusionWallLeftShadow,  // exclude Left Shower Wall Shadow
      this.zIndexes[layoutType].Shower.Custom60in.ExtrusionWallLeftShadowHalfWall,  // exclude Left Shower Wall Shadow
      this.zIndexes[layoutType].Shower.Custom60in.ShowerDrain,  // exclude Shower Drain
      this.zIndexes[layoutType].Shower.Custom60in.PonyWallCapShadow,  // exclude Pony Wall Cap Shadow
      this.zIndexes[layoutType].Shower.Custom60in.WallShadow,  // exclude Shower Interior shadow
      this.zIndexes[layoutType].Shower.Custom60in.PonyWallShadow,  // exclude Pony Wall Overlay
      this.zIndexes[layoutType].Shower.Custom60in.ThresholdShadow,  // exclude ThresholdShadow
      this.zIndexes[layoutType].Shower.Custom60in.FloorShadow,  // exclude ThresholdShadow
    ]
  }

}
